// ─── 🎾 La valutazione mandata a Level Akinator, nella scheda del socio (22/10/2026) ───
//
// Mockup «Valutazioni a Level Akinator», *«1A»*: ogni livello che la Segreteria cambia va a Level Akinator come
// valutazione del circolo. La riga dice se è da mandare, mandata o da controllare; «🗑️ Togli la valutazione» la
// ritira (il livello in scheda resta com'è). ⛔ Una valutazione senza conferma non riparte da sola: conterebbe
// due volte. La rimanda la Segreteria, con «🔁 Manda di nuovo».

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { livelloInParole, type ValutazioneDelSocio } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';
import { usaScalaDeiLivelli } from '../../features/soci/dati.js';

const giorno = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '');

export function ValutazioneAkinator({
  id,
  modificabile,
}: {
  id: string;
  modificabile: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  const v = useQuery({
    queryKey: ['valutazione-akinator', id],
    queryFn: () => chiama<{ valutazione: ValutazioneDelSocio }>('GET', `/members/${id}/akinator-assessment`),
    // ⏳ «Parte entro qualche minuto»: la riga si rilegge da sé finché c'è qualcosa in viaggio.
    refetchInterval: (q) =>
      ['da_mandare', 'da_togliere'].includes(q.state.data?.valutazione?.state ?? '') ? 30_000 : false,
  });
  const gesto = useMutation({
    meta: {
      fatto: (_d: unknown, g: unknown) => (g === 'remove' ? 'valutazione tolta' : 'valutazione rimandata'),
    },
    mutationFn: (g: 'remove' | 'resend') =>
      chiama<{ valutazione: ValutazioneDelSocio }>('POST', `/members/${id}/akinator-assessment/${g}`),
    onSuccess: (dopo) => qc.setQueryData(['valutazione-akinator', id], dopo),
    onError: () => void qc.invalidateQueries({ queryKey: ['valutazione-akinator', id] }),
  });
  if (!v.data) return <Guasto errore={v.error} />;
  const x = v.data.valutazione;
  if (!x) return null;
  const livello = <strong>{livelloInParole(fasce, x.level)}</strong>;
  const togli = modificabile && ['mandata', 'incerta', 'rifiutata', 'da_mandare'].includes(x.state);
  const rimanda = modificabile && ['incerta', 'rifiutata'].includes(x.state);
  return (
    <section className="riquadro">
      <h4>🎾 Level Akinator</h4>
      {x.state === 'da_mandare' && (
        <>
          <p className="piccolo">⏳ Valutazione da mandare a Level Akinator: {livello}</p>
          <p className="tenue piccolo">Parte entro qualche minuto.</p>
        </>
      )}
      {x.state === 'mandata' && (
        <>
          <p className="piccolo">
            ✅ Valutazione mandata a Level Akinator il {giorno(x.sentAt)}: {livello}
          </p>
          <p className="tenue piccolo">Serve a rendere più giuste le domande del livello.</p>
        </>
      )}
      {x.state === 'da_togliere' && (
        <>
          <p className="piccolo">⏳ Valutazione da togliere da Level Akinator: {livello}</p>
          <p className="tenue piccolo">Parte entro qualche minuto. Il livello in scheda resta com’è.</p>
        </>
      )}
      {x.state === 'tolta' && (
        <p className="piccolo">
          Valutazione tolta da Level Akinator il {giorno(x.removedAt)}. Il livello in scheda resta com’è.
        </p>
      )}
      {x.state === 'incerta' && (
        <>
          <p className="piccolo">⚠️ Valutazione mandata a Level Akinator, senza conferma: {livello}</p>
          <p className="tenue piccolo">
            La risposta è mancata: forse è già arrivata. Per non contarla due volte, riparte solo se lo decidi
            tu.
          </p>
        </>
      )}
      {x.state === 'rifiutata' && (
        <>
          <p className="piccolo">⚠️ Level Akinator ha rifiutato la valutazione: {livello}</p>
          {x.lastError && <p className="tenue piccolo">Motivo: {x.lastError}</p>}
        </>
      )}
      <Guasto errore={gesto.error} />
      {(togli || rimanda) && (
        <div className="azioni">
          {rimanda && (
            <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('resend')}>
              🔁 Manda di nuovo
            </button>
          )}
          {togli && (
            <button
              type="button"
              className="piatto"
              disabled={gesto.isPending}
              onClick={() => gesto.mutate('remove')}
            >
              🗑️ Togli la valutazione
            </button>
          )}
        </div>
      )}
    </section>
  );
}
